/**
 * The Story — the Bible read as one narrative rather than as sixty-six
 * separate books.
 *
 * Seven acts, four chapters each. A chapter is an illuminated plate, six prose
 * beats, an anchor verse from the main deck, and three or four questions that
 * gate the next chapter. The point is the through-line: someone who walks the
 * whole path should be able to say what the Bible is *about*, not just recite
 * verses from it.
 *
 * ── Why this file is only an index ──────────────────────────────────────────
 *
 * The acts live one to a file. That is the unit they are written and reviewed
 * in — four chapters that share a subject and want to share a voice — and at
 * roughly 300 lines of prose each, seven of them in one file is a file nobody
 * reads to the end of. What the rest of the app sees is the shape below, and
 * that has not changed.
 *
 * ── The two ids that must never change ──────────────────────────────────────
 *
 *   chapter.id   progress is persisted against it. Rename one and every player
 *                who finished that chapter is quietly told they have not.
 *   question.id  answered-question history is keyed on it, across the whole
 *                app — so these must be unique against practice questions too.
 *
 * Structure:
 *   act.id / title / summary / span
 *   act.chapters[]      in order; each gated on the previous
 *
 *   chapter.id          stable slug
 *   chapter.title       short, concrete
 *   chapter.where       the passage it retells
 *   chapter.era         a rough time marker for the timeline rail
 *   chapter.artCaption  one line under the plate
 *   chapter.scenes[]    six prose beats, shown one at a time
 *   chapter.verseId     anchor verse, must exist in verses.js
 *   chapter.takeaway    the one sentence to carry forward
 *   chapter.questions[] the gate: three or four, escalating
 *
 * Scripture quoted inside scenes is the World English Bible, public domain,
 * same as the verse deck. Nothing here quotes a modern translation, because
 * this ships publicly and those are somebody's copyright.
 */

import { INTROS } from './story/intros.js';
import { ACT_BEGINNINGS } from './story/act-beginnings.js';
import { ACT_FAMILY } from './story/act-family.js';
import { ACT_RESCUE } from './story/act-rescue.js';
import { ACT_KINGDOM } from './story/act-kingdom.js';
import { ACT_EXILE } from './story/act-exile.js';
import { ACT_WORD } from './story/act-word.js';
import { ACT_CHURCH } from './story/act-church.js';

export const ACTS = [
  ACT_BEGINNINGS,
  ACT_FAMILY,
  ACT_RESCUE,
  ACT_KINGDOM,
  ACT_EXILE,
  ACT_WORD,
  ACT_CHURCH,
];

/** Flat, ordered list of every chapter, with its act stitched on. */
export const CHAPTERS = ACTS.flatMap((act, actIndex) =>
  act.chapters.map((chapter, chapterIndex) => ({
    ...chapter,
    // Written as a set, in one file, then stitched on here. See intros.js.
    intro: INTROS[chapter.id] ?? null,
    actId: act.id,
    actTitle: act.title,
    actIndex,
    chapterIndex,
    /** 1-based position across the whole story, used for the map numbering. */
    order: ACTS.slice(0, actIndex).reduce((n, a) => n + a.chapters.length, 0) + chapterIndex + 1,
  })),
);

export const CHAPTERS_BY_ID = Object.fromEntries(CHAPTERS.map((c) => [c.id, c]));

export const STORY_TOTAL = CHAPTERS.length;

/** Every story question, flattened — for counts and for the practice decks. */
export const STORY_QUESTIONS = CHAPTERS.flatMap((chapter) =>
  chapter.questions.map((question) => ({ ...question, chapterId: chapter.id })),
);
